/**
 * Virtual Asset Test Mock Data Generator
 *
 * 虚拟资产知识测试Mock数据生成器
 * 根据目标分数自动选择合适的答案
 *
 * @module mockDataGenerator.virtual-asset-test
 */

export interface QuizDataContent {
  category: string;
  "no.": number;
  questions: QuizQuestion[];
}

export interface QuizQuestion {
  "no.": number;
  options: QuizQuestionOption[];
  question: string;
}

export interface QuizQuestionOption {
  answer: number;
  "no.": number;
  option: string;
}

/**
 * Mock配置选项
 */
export interface VirtualAssetMockConfig {
  /**
   * 目标总分 (0-100分)
   * 每道题10分，共10道题
   */
  targetScore: number;
}

/**
 * 预设场景配置
 */
export const VIRTUAL_ASSET_PRESETS = {
  /** 不及格 - 30分 */
  FAIL_LOW: {
    targetScore: 30,
    name: "不及格（30分）",
    description: "答对3道题，未通过测试",
  },
  /** 不及格 - 60分 */
  FAIL_HIGH: {
    targetScore: 60,
    name: "不及格（60分）",
    description: "答对6道题，接近及格线",
  },
  /** 刚好及格 - 70分 */
  PASS: {
    targetScore: 70,
    name: "及格（70分）",
    description: "答对7道题，刚好通过",
  },
  /** 良好 - 80分 */
  GOOD: {
    targetScore: 80,
    name: "良好（80分）",
    description: "答对8道题，表现良好",
  },
  /** 优秀 - 100分 */
  EXCELLENT: {
    targetScore: 100,
    name: "优秀（100分）",
    description: "全部答对，完美通过",
  },
} as const;

/**
 * 生成虚拟资产测试Mock数据
 *
 * @param questions - 题目数据
 * @param config - Mock配置
 * @returns 表单数据对象
 */
export function generateVirtualAssetMockData(
  questions: QuizQuestion[],
  config: VirtualAssetMockConfig
): Record<string, any> {
  const { targetScore } = config;

  if (!questions || questions.length === 0) {
    return {};
  }

  // 限制目标分数在0-100之间
  const validTargetScore = Math.max(0, Math.min(100, targetScore));

  // 计算需要答对多少道题（每道题10分）
  const targetCorrectCount = Math.floor(validTargetScore / 10);

  const formData: Record<string, any> = {};

  // 为前N道题选择正确答案（answer=10），其余选择错误答案（answer=0）
  questions.forEach((question: QuizQuestion, index: number) => {
    const fieldName = `question_${question["no."]}`;

    if (index < targetCorrectCount) {
      // 选择正确答案（answer值最大的选项）
      const correctOption = question.options.reduce(
        (max: QuizQuestionOption, current: QuizQuestionOption) =>
          current.answer > max.answer ? current : max
      );
      formData[fieldName] = correctOption["no."];
    } else {
      // 选择错误答案（answer值最小的选项）
      const wrongOption = question.options.reduce(
        (min: QuizQuestionOption, current: QuizQuestionOption) =>
          current.answer < min.answer ? current : min
      );
      formData[fieldName] = wrongOption["no."];
    }
  });

  return formData;
}

/**
 * 计算当前答案的总分
 *
 * @param questions - 题目数据
 * @param formValues - 表单值
 * @returns 总分
 */
export function calculateVirtualAssetScore(
  questions: QuizQuestion[],
  formValues: Record<string, any>
): number {
  let totalScore = 0;

  questions.forEach((question: QuizQuestion) => {
    const fieldName = `question_${question["no."]}`;
    const selectedOptionNo = formValues[fieldName];

    if (selectedOptionNo !== undefined) {
      const selectedOption = question.options.find(
        (opt) => opt["no."] === selectedOptionNo
      );
      if (selectedOption) {
        totalScore += selectedOption.answer;
      }
    }
  });

  return totalScore;
}

/**
 * 生成随机分数（在合理范围内）
 *
 * @returns 随机分数 (0-100)
 */
export function generateRandomScore(): number {
  // 生成0-10之间的随机整数，代表答对的题目数
  const correctCount = Math.floor(Math.random() * 11);
  return correctCount * 10;
}
